import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useRenameBranchMutation, type Branch } from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog'

export interface BranchRenameDialogProps {
  branch: Branch
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Rename dialog for an active, unprotected branch. Registry metadata only —
 * commits, the head pointer, and the working database are keyed by branch id,
 * so nothing else moves.
 */
export function BranchRenameDialog({ branch, open, onOpenChange }: BranchRenameDialogProps) {
  const [renameBranch, renameState] = useRenameBranchMutation()
  const [name, setName] = React.useState(branch.name)
  const [submitError, setSubmitError] = React.useState('')

  React.useEffect(() => {
    if (!open) {
      setName(branch.name)
      setSubmitError('')
      renameState.reset()
    }
  }, [open, branch.name, renameState])

  const trimmed = name.trim()
  const submitting = renameState.isLoading
  const canSubmit = trimmed.length > 0 && trimmed !== branch.name && !submitting

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit) return
    setSubmitError('')
    try {
      const renamed = await renameBranch({ id: branch.id, name: trimmed }).unwrap()
      toast.success(`Renamed ${branch.name} to ${renamed.name}`)
      onOpenChange(false)
    } catch (err) {
      setSubmitError(extractRenameError(err))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Rename {branch.name}</DialogTitle>
          <DialogDescription>
            Renames the branch only — its history, head commit, and working copy are unaffected.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <Input
            value={name}
            onChange={(event) => {
              setName(event.target.value)
              setSubmitError('')
            }}
            placeholder="branch-name"
            aria-label="New branch name"
            autoComplete="off"
            autoFocus
            className="font-mono"
            disabled={submitting}
          />

          {submitError ? (
            <p role="alert" className="text-sm text-st-down">
              {submitError}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
              Rename branch
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function extractRenameError(err: unknown): string {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: unknown }).data
    if (data && typeof data === 'object' && 'message' in data) {
      const message = (data as { message?: unknown }).message
      if (typeof message === 'string' && message.length > 0) return message
    }
  }
  return 'Could not rename branch.'
}
